// ---------------------------------------------------------------------------
// Felton Discovery -- Constants
// ---------------------------------------------------------------------------
// All constant values for the Discovery feature.
// Sourced from the UX spec (03-UX-FELTON.md).
// ---------------------------------------------------------------------------

import type {
  TimePeriod,
  TimePeriodOption,
  XtdOptionConfig,
  DataSourceId,
  DataSourceConfig,
  PlatformId,
  PlatformConfig,
  AgeLabel,
  FollowerTierConfig,
  FilterDimension,
  ColumnConfig,
  EnhancedColumnConfig,
  TrendMetric,
  ViewMode,
} from './types';

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export const DEFAULT_PERIOD: TimePeriod = '30d';
export const DEFAULT_DATA_SOURCE: DataSourceId = 'profiles';
export const DEFAULT_VIEW: ViewMode = 'table';
export const DEFAULT_PAGE_SIZE = 25;
export const SEARCH_DEBOUNCE_MS = 300;

// ---------------------------------------------------------------------------
// Time Periods
// ---------------------------------------------------------------------------

export const TIME_PERIODS: ReadonlyArray<TimePeriodOption> = [
  { id: 'custom', label: 'Custom', shortLabel: 'Custom' },
  { id: 'today', label: 'Today', shortLabel: 'Today' },
  { id: 'yesterday', label: 'Yesterday', shortLabel: 'Yest.' },
  { id: '7d', label: 'Last 7 Days', shortLabel: '7D' },
  { id: '15d', label: 'Last 15 Days', shortLabel: '15D' },
  { id: '30d', label: 'Last 30 Days', shortLabel: '30D' },
  { id: '3m', label: 'Last 3 Months', shortLabel: '3M' },
  { id: '6m', label: 'Last 6 Months', shortLabel: '6M' },
  { id: '12m', label: 'Last 12 Months', shortLabel: '12M' },
] as const;

export const XTD_OPTIONS: ReadonlyArray<XtdOptionConfig> = [
  { id: 'wtd', label: 'Week to Date', shortLabel: 'WTD' },
  { id: 'mtd', label: 'Month to Date', shortLabel: 'MTD' },
  { id: 'qtd', label: 'Quarter to Date', shortLabel: 'QTD' },
  { id: 'ytd', label: 'Year to Date', shortLabel: 'YTD' },
] as const;

/** All valid time period identifiers (useful for validation) */
export const VALID_PERIODS: ReadonlyArray<TimePeriod> = [
  ...TIME_PERIODS.map(tp => tp.id),
  ...XTD_OPTIONS.map(x => x.id),
  'all',
] as const;

// ---------------------------------------------------------------------------
// Data Sources
// ---------------------------------------------------------------------------

export const DATA_SOURCES: ReadonlyArray<DataSourceConfig> = [
  {
    id: 'profiles',
    icon: '\u{1F464}',
    label: 'Profiles',
    count: '2.76M',
    searchPlaceholder: 'Search by username...',
  },
  {
    id: 'content',
    icon: '\u{1F4C4}',
    label: 'Content',
    count: '187K',
    searchPlaceholder: 'Search by caption or author...',
  },
  {
    id: 'comments',
    icon: '\u{1F4AC}',
    label: 'Comments',
    count: '7.8M',
    searchPlaceholder: 'Search by comment text...',
  },
  {
    id: 'brands',
    icon: '\u{1F3F7}\u{FE0F}',
    label: 'Brands',
    count: '11.5K',
    searchPlaceholder: 'Search by brand name...',
  },
  // {
  //   id: 'hashtags',
  //   icon: '#',
  //   label: 'Hashtags',
  //   count: '35K',
  //   searchPlaceholder: 'Search by hashtag...',
  // }, // DISABLED: No data in database (posts_hashtag table is empty)
  {
    id: 'teams',
    icon: '\u{26BD}',
    label: 'Teams',
    count: '5.4K',
    searchPlaceholder: 'Search by team name...',
  },
  // {
  //   id: 'channels',
  //   icon: '\u{1F4FA}',
  //   label: 'Channels',
  //   count: '20',
  //   searchPlaceholder: 'Search by channel name...',
  // }, // DISABLED: Channels has its own dedicated page
] as const;

/** All valid data source identifiers */
export const VALID_DATA_SOURCES: ReadonlyArray<DataSourceId> = DATA_SOURCES.map(ds => ds.id);

// ---------------------------------------------------------------------------
// Platforms
// ---------------------------------------------------------------------------

export const PLATFORMS: ReadonlyArray<PlatformConfig> = [
  { id: 'instagram', label: 'Instagram', icon: 'instagram' },
  { id: 'x', label: 'X', icon: 'x' },
  { id: 'tiktok', label: 'TikTok', icon: 'tiktok' },
  { id: 'youtube', label: 'YouTube', icon: 'youtube' },
] as const;

export const VALID_PLATFORMS: ReadonlyArray<PlatformId> = PLATFORMS.map(p => p.id);

// ---------------------------------------------------------------------------
// Age Labels (Marketing)
// ---------------------------------------------------------------------------

export const AGE_LABELS: ReadonlyArray<AgeLabel> = [
  { marketing: 'Toddlers', range: '0-3', ageIntervalIds: [1] },
  { marketing: 'Kids', range: '4-12', ageIntervalIds: [2, 3] },
  { marketing: 'Teen', range: '13-17', ageIntervalIds: [4] },
  { marketing: 'Gen Z', range: '18-24', ageIntervalIds: [5] },
  { marketing: 'Millennials', range: '25-35', ageIntervalIds: [6, 7] },
  { marketing: 'Gen X', range: '36-45', ageIntervalIds: [8, 9] },
  { marketing: 'Gen X+', range: '46-60', ageIntervalIds: [10, 11] },
  { marketing: 'Boomers', range: '61-70', ageIntervalIds: [12] },
  { marketing: 'Boomers+', range: '71+', ageIntervalIds: [13] },
] as const;

// ---------------------------------------------------------------------------
// Follower Tiers
// ---------------------------------------------------------------------------

export const FOLLOWER_TIERS: ReadonlyArray<FollowerTierConfig> = [
  { id: 'nano', label: 'Nano', range: '1K-10K' },
  { id: 'micro', label: 'Micro', range: '10K-50K' },
  { id: 'mid', label: 'Mid', range: '50K-500K' },
  { id: 'macro', label: 'Macro', range: '500K-1M' },
  { id: 'mega', label: 'Mega', range: '1M+' },
] as const;

// ---------------------------------------------------------------------------
// Filter Dimensions (More Filters panel)
// ---------------------------------------------------------------------------

export const FILTER_DIMENSIONS: ReadonlyArray<FilterDimension> = [
  // Demographics
  {
    key: 'gender',
    label: 'Gender',
    category: 'demographics',
    type: 'multi-select',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'age',
    label: 'Generation',
    category: 'demographics',
    type: 'multi-select',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'ethnicity',
    label: 'Ethnicity',
    category: 'demographics',
    type: 'multi-select',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'country',
    label: 'Country',
    category: 'demographics',
    type: 'searchable-multi-select',
    applicableTo: ['profiles', 'content', 'comments', 'teams', 'brands'],
  },
  {
    key: 'followerTier',
    label: 'Follower Tier',
    category: 'demographics',
    type: 'multi-select',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'industry',
    label: 'Industry',
    category: 'demographics',
    type: 'multi-select',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },

  // Psychographics
  {
    key: 'lifestyle',
    label: 'Lifestyle',
    category: 'psychographics',
    type: 'multi-select',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'statusClass',
    label: 'Status Class',
    category: 'psychographics',
    type: 'multi-select',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },

  // Behaviour
  {
    key: 'emotions',
    label: 'Emotions',
    category: 'behaviour',
    type: 'multi-select',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'sentiment',
    label: 'Sentiment',
    category: 'behaviour',
    type: 'range',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'language',
    label: 'Language',
    category: 'behaviour',
    type: 'searchable-multi-select',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },

  // Content
  {
    key: 'categories',
    label: 'Categories',
    category: 'content',
    type: 'multi-select',
    applicableTo: ['profiles', 'content', 'comments', 'teams'],
  },
  {
    key: 'mediaType',
    label: 'Media Type',
    category: 'content',
    type: 'multi-select',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'paidOrganic',
    label: 'Paid/Organic',
    category: 'content',
    type: 'toggle',
    applicableTo: ['content'],
  },

  // Entities
  {
    key: 'brands',
    label: 'Brands',
    category: 'entities',
    type: 'searchable-multi-select',
    applicableTo: ['profiles', 'content', 'comments', 'teams'],
  },
  {
    key: 'brandCategory',
    label: 'Brand Category',
    category: 'entities',
    type: 'multi-select',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'teams',
    label: 'Teams',
    category: 'entities',
    type: 'searchable-multi-select',
    applicableTo: ['profiles', 'content', 'comments', 'brands'],
  },
  {
    key: 'hashtags',
    label: 'Hashtags',
    category: 'entities',
    type: 'searchable-multi-select',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },

  // Quality
  {
    key: 'isPerson',
    label: 'Is Person',
    category: 'quality',
    type: 'toggle',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'verified',
    label: 'Verified',
    category: 'quality',
    type: 'toggle',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'famous',
    label: 'Famous',
    category: 'quality',
    type: 'toggle',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'spam',
    label: 'Spam',
    category: 'quality',
    type: 'toggle',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'toxic',
    label: 'Toxic',
    category: 'quality',
    type: 'toggle',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
] as const;

// ---------------------------------------------------------------------------
// Trend Metrics per Data Source
// ---------------------------------------------------------------------------

export const TREND_METRICS_BY_SOURCE: Readonly<
  Record<DataSourceId, ReadonlyArray<{ readonly id: TrendMetric; readonly label: string }>>
> = {
  profiles: [
    { id: 'new_profiles', label: 'Active Audience' },
    { id: 'comments', label: 'Comments' },
    { id: 'avg_sentiment', label: 'Avg. Sentiment' },
  ],
  content: [
    { id: 'likes', label: 'Likes' },
    { id: 'views', label: 'Views' },
    { id: 'comments', label: 'Comments' },
    { id: 'engagement_rate', label: 'Engagement Rate' },
    { id: 'shares', label: 'Shares' },
  ],
  comments: [
    { id: 'volume', label: 'Comments Volume' },
    { id: 'sentiment', label: 'Sentiment' },
    { id: 'comment_likes', label: 'Comment Likes' },
  ],
  brands: [
    // { id: 'total_detections', label: 'Total Detections' },
    { id: 'unique_profiles', label: 'Unique Profiles' },
    { id: 'audience_comments', label: 'Audience Comments' },
  ],
  hashtags: [
    { id: 'uses', label: 'Uses' },
    { id: 'posts_count', label: 'Posts' },
    { id: 'engagement', label: 'Engagement' },
  ],
  teams: [
    // { id: 'total_mentions', label: 'Total Mentions' },
    { id: 'profiles_count', label: 'Profiles' },
    { id: 'comment_mentions', label: 'Comment Mentions' },
  ],
  channels: [
    { id: 'posts', label: 'Posts' },
    { id: 'likes', label: 'Likes' },
    { id: 'comments', label: 'Comments' },
    { id: 'interactions', label: 'Interactions' },
  ],
};

// ---------------------------------------------------------------------------
// Column Configs per Data Source
// ---------------------------------------------------------------------------

export const COLUMNS_PROFILES: ReadonlyArray<EnhancedColumnConfig> = [
  // Profile ID (hidden by default, shown when identity columns are masked)
  {
    key: 'id',
    label: 'ID',
    sortable: false,
    align: 'left',
    width: '80px',
    defaultVisible: false,
    chartType: 'none',
    cellRenderer: 'mutedId',
  },
  // Identity (3)
  {
    key: 'avatar',
    label: 'Avatar',
    sortable: false,
    align: 'left',
    width: '60px',
    defaultVisible: true,
    chartType: 'none',
    cellRenderer: 'avatar',
  },
  {
    key: 'username',
    label: 'Username',
    sortable: true,
    align: 'left',
    defaultVisible: true,
    chartType: 'none',
    cellRenderer: 'externalProfileLink',
  },
  {
    key: 'platform',
    label: 'Platform',
    sortable: true,
    align: 'left',
    width: '100px',
    defaultVisible: true,
    chartType: 'column',
    cellRenderer: 'platform',
  },

  // Followers (numeric count)
  {
    key: 'followers',
    label: 'Followers',
    sortable: true,
    align: 'left',
    width: '110px',
    defaultVisible: true,
    chartType: 'bar',
    cellRenderer: 'compactNumber',
  },

  // Demographics (1)
  {
    key: 'followerTier',
    label: 'Follower Tier',
    sortable: true,
    align: 'left',
    width: '120px',
    defaultVisible: true,
    chartType: 'donut',
    cellRenderer: 'text',
  },

  // Associations (2)
  {
    key: 'brands',
    label: 'Brands',
    sortable: true,
    align: 'left',
    width: '90px',
    defaultVisible: true,
    chartType: 'bar',
    cellRenderer: 'number',
  },
  {
    key: 'teams',
    label: 'Teams',
    sortable: true,
    align: 'left',
    width: '90px',
    defaultVisible: true,
    chartType: 'bar',
    cellRenderer: 'number',
  },

  // Engagement (3) - posts and comments support drill-down
  {
    key: 'posts',
    label: 'Posts',
    sortable: true,
    align: 'left',
    width: '90px',
    defaultVisible: true,
    chartType: 'none',
    cellRenderer: 'drillDownNumber',
  },
  {
    key: 'comments',
    label: 'Comments',
    sortable: true,
    align: 'left',
    width: '140px',
    defaultVisible: true,
    chartType: 'none',
    cellRenderer: 'commentsWithAnalyzed',
  },

  // Sentiment & Emotion (3)
  {
    key: 'sentiment',
    label: 'Sentiment',
    sortable: true,
    align: 'left',
    width: '100px',
    defaultVisible: true,
    chartType: 'gauge',
    cellRenderer: 'sentiment',
  },
  {
    key: 'emotionCategory',
    label: 'Emotion Category',
    sortable: true,
    align: 'left',
    width: '180px',
    defaultVisible: true,
    chartType: 'radar',
    cellRenderer: 'text',
  },
  {
    key: 'emotion',
    label: 'Last Emotion',
    sortable: true,
    align: 'left',
    defaultVisible: true,
    chartType: 'radar',
    cellRenderer: 'emotion',
  },
] as const;

export const COLUMNS_CONTENT: ReadonlyArray<EnhancedColumnConfig> = [
  // Visual & Identity (3)
  {
    key: 'thumbnail',
    label: 'Thumbnail',
    sortable: false,
    align: 'left',
    width: '80px',
    defaultVisible: true,
    chartType: 'none',
    cellRenderer: 'thumbnail',
  },
  {
    key: 'caption',
    label: 'Caption',
    sortable: true,
    align: 'left',
    width: '280px',
    defaultVisible: true,
    chartType: 'none',
    cellRenderer: 'externalPostLink',
    cellOptions: { maxLength: Infinity },
  },
  {
    key: 'author',
    label: 'Channel',
    sortable: true,
    align: 'left',
    defaultVisible: true,
    chartType: 'none',
    cellRenderer: 'externalProfileLink',
  },

  // Platform & Classification (3)
  {
    key: 'platform',
    label: 'Platform',
    sortable: true,
    align: 'left',
    width: '100px',
    defaultVisible: true,
    chartType: 'column',
    cellRenderer: 'platform',
  },
  {
    key: 'mediaType',
    label: 'Media Type',
    sortable: true,
    align: 'left',
    width: '120px',
    defaultVisible: true,
    chartType: 'donut',
    cellRenderer: 'mediaType',
  },
  {
    key: 'isVideo',
    label: 'Video',
    sortable: true,
    align: 'left',
    width: '80px',
    defaultVisible: false,
    chartType: 'donut',
    cellRenderer: 'boolean',
  },
  {
    key: 'category',
    label: 'Category',
    sortable: true,
    align: 'left',
    defaultVisible: true,
    chartType: 'bar',
    cellRenderer: 'text',
  },

  // Engagement Metrics (5)
  {
    key: 'likes',
    label: 'Likes',
    sortable: true,
    align: 'left',
    width: '90px',
    defaultVisible: true,
    chartType: 'none',
    cellRenderer: 'number',
  },
  {
    key: 'comments',
    label: 'Comments',
    sortable: true,
    align: 'left',
    width: '140px',
    defaultVisible: true,
    chartType: 'none',
    cellRenderer: 'commentsWithAnalyzed',
  },
  {
    key: 'shares',
    label: 'Shares',
    sortable: true,
    align: 'left',
    width: '90px',
    defaultVisible: false,
    chartType: 'none',
    cellRenderer: 'number',
  },
  {
    key: 'views',
    label: 'Views',
    sortable: true,
    align: 'left',
    width: '90px',
    defaultVisible: false,
    chartType: 'none',
    cellRenderer: 'number',
  },
  {
    key: 'engagement',
    label: 'Engagement',
    sortable: true,
    align: 'left',
    width: '110px',
    defaultVisible: true,
    chartType: 'gauge',
    cellRenderer: 'engagement',
  },

  // Performance & Sentiment (3)
  {
    key: 'performance',
    label: 'Performance',
    sortable: true,
    align: 'left',
    width: '110px',
    defaultVisible: false,
    chartType: 'donut',
    cellRenderer: 'performance',
  },
  {
    key: 'sentiment',
    label: 'Sentiment',
    sortable: true,
    align: 'left',
    width: '100px',
    defaultVisible: true,
    chartType: 'gauge',
    cellRenderer: 'sentimentText',
  },
  {
    key: 'emotion',
    label: 'Emotion',
    sortable: true,
    align: 'left',
    width: '100px',
    defaultVisible: false,
    chartType: 'bar',
    cellRenderer: 'emotion',
  },

  // Author Demographics (2)
  {
    key: 'authorFollowers',
    label: 'Author Followers',
    sortable: true,
    align: 'left',
    width: '140px',
    defaultVisible: false,
    chartType: 'none',
    cellRenderer: 'number',
  },
  {
    key: 'authorCountry',
    label: 'Author Country',
    sortable: true,
    align: 'left',
    width: '130px',
    defaultVisible: false,
    chartType: 'bar',
    cellRenderer: 'text',
  },

  // Date (2)
  {
    key: 'date',
    label: 'Date',
    sortable: true,
    align: 'left',
    width: '120px',
    defaultVisible: true,
    chartType: 'none',
    cellRenderer: 'date',
  },
  {
    key: 'updatedAt',
    label: 'Updated',
    sortable: true,
    align: 'left',
    width: '120px',
    defaultVisible: true,
    chartType: 'none',
    cellRenderer: 'date',
  },
] as const;

export const COLUMNS_COMMENTS: ReadonlyArray<EnhancedColumnConfig> = [
  {
    key: 'author',
    label: 'Profile',
    sortable: true,
    align: 'left',
    defaultVisible: true,
    chartType: 'bar',
    cellRenderer: 'externalProfileLink',
  },
  {
    key: 'text',
    label: 'Text',
    sortable: false,
    align: 'left',
    defaultVisible: true,
    chartType: 'none',
  },
  {
    key: 'post',
    label: 'Post',
    sortable: false,
    align: 'left',
    width: '280px',
    defaultVisible: true,
    chartType: 'none',
    cellRenderer: 'externalPostLink',
    cellOptions: { maxLength: Infinity },
  },
  {
    key: 'platform',
    label: 'Platform',
    sortable: true,
    align: 'left',
    width: '100px',
    defaultVisible: true,
    chartType: 'column',
    cellRenderer: 'platform',
  },
  {
    key: 'sentiment',
    label: 'Sentiment',
    sortable: true,
    align: 'left',
    width: '100px',
    defaultVisible: true,
    chartType: 'gauge',
    cellRenderer: 'sentiment',
  },
  {
    key: 'emotion',
    label: 'Emotion',
    sortable: true,
    align: 'left',
    defaultVisible: true,
    chartType: 'radar',
    cellRenderer: 'emotion',
  },
  {
    key: 'language',
    label: 'Language',
    sortable: true,
    align: 'left',
    defaultVisible: true,
    chartType: 'bar',
    cellRenderer: 'text',
  },
  {
    key: 'toxic',
    label: 'Toxic',
    sortable: true,
    align: 'left',
    width: '80px',
    defaultVisible: true,
    chartType: 'kpi',
    cellRenderer: 'boolean',
  },
  {
    key: 'spam',
    label: 'Spam',
    sortable: true,
    align: 'left',
    width: '80px',
    defaultVisible: true,
    chartType: 'kpi',
    cellRenderer: 'boolean',
  },
  {
    key: 'commentLikes',
    label: 'Comment Likes',
    sortable: true,
    align: 'left',
    width: '120px',
    defaultVisible: true,
    chartType: 'none',
    cellRenderer: 'number',
  },
  {
    key: 'replies',
    label: 'Replies',
    sortable: true,
    align: 'left',
    width: '100px',
    defaultVisible: false,
    chartType: 'none',
    cellRenderer: 'number',
  },
  {
    key: 'date',
    label: 'Date',
    sortable: true,
    align: 'left',
    width: '120px',
    defaultVisible: true,
    chartType: 'histogram',
    cellRenderer: 'date',
  },
] as const;

export const COLUMNS_BRANDS: ReadonlyArray<EnhancedColumnConfig> = [
  // Identity (1)
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    align: 'left',
    defaultVisible: true,
    chartType: 'bar',
    cellRenderer: 'text',
  },

  // Classification (2)
  {
    key: 'category',
    label: 'Category',
    sortable: true,
    align: 'left',
    width: '140px',
    defaultVisible: true,
    chartType: 'column',
    cellRenderer: 'text',
  },
  {
    key: 'subcategory',
    label: 'Subcategory',
    sortable: true,
    align: 'left',
    width: '150px',
    defaultVisible: true,
    chartType: 'bar',
    cellRenderer: 'text',
  },

  // Detection Metrics (2)
  {
    key: 'totalDetections',
    label: 'Total Detections',
    sortable: true,
    align: 'left',
    width: '140px',
    defaultVisible: false,
    chartType: 'donut',
    cellRenderer: 'number',
  },

  // Profile Count (1)
  {
    key: 'profiles',
    label: 'Profiles',
    sortable: true,
    align: 'left',
    width: '100px',
    defaultVisible: true,
    chartType: 'donut',
    cellRenderer: 'drillDownNumber',
  },
] as const;

export const COLUMNS_HASHTAGS: ReadonlyArray<EnhancedColumnConfig> = [
  {
    key: 'hashtag',
    label: 'Hashtag',
    sortable: true,
    align: 'left',
    defaultVisible: true,
    chartType: 'none',
    cellRenderer: 'text',
  },
  {
    key: 'posts',
    label: 'Posts',
    sortable: true,
    align: 'right',
    width: '100px',
    defaultVisible: true,
    chartType: 'none',
    cellRenderer: 'number',
  },
  {
    key: 'comments',
    label: 'Comments',
    sortable: true,
    align: 'right',
    width: '100px',
    defaultVisible: true,
    chartType: 'none',
    cellRenderer: 'number',
  },
  {
    key: 'total',
    label: 'Total',
    sortable: true,
    align: 'right',
    width: '100px',
    defaultVisible: true,
    chartType: 'none',
    cellRenderer: 'number',
  },
  {
    key: 'platforms',
    label: 'Platforms',
    sortable: false,
    align: 'center',
    defaultVisible: true,
    chartType: 'donut',
    cellRenderer: 'platforms',
  },
] as const;

export const COLUMNS_TEAMS: ReadonlyArray<EnhancedColumnConfig> = [
  // Identity & Type (2)
  {
    key: 'type',
    label: 'Type',
    sortable: true,
    align: 'left',
    width: '90px',
    defaultVisible: true,
    chartType: 'donut',
    cellRenderer: 'teamType',
  },
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    align: 'left',
    defaultVisible: true,
    chartType: 'bar',
    cellRenderer: 'text',
  },

  // Mentions Metrics (1)
  {
    key: 'totalMentions',
    label: 'Total Mentions',
    sortable: true,
    align: 'left',
    width: '130px',
    defaultVisible: false,
    chartType: 'donut',
    cellRenderer: 'number',
  },

  // Profile Count (1)
  {
    key: 'profiles',
    label: 'Profiles',
    sortable: true,
    align: 'left',
    width: '100px',
    defaultVisible: true,
    chartType: 'donut',
    cellRenderer: 'drillDownNumber',
  },
] as const;

export const COLUMNS_CHANNELS: ReadonlyArray<EnhancedColumnConfig> = [
  // Identity
  {
    key: 'profilePicUrl',
    label: '',
    sortable: false,
    align: 'center',
    width: '50px',
    defaultVisible: true,
    chartType: 'none',
    cellRenderer: 'avatar',
  },
  {
    key: 'datasourceName',
    label: 'Channel',
    sortable: true,
    align: 'left',
    defaultVisible: true,
    chartType: 'none',
    cellRenderer: 'text',
  },
  {
    key: 'platform',
    label: 'Platform',
    sortable: true,
    align: 'center',
    width: '100px',
    defaultVisible: true,
    chartType: 'donut',
    cellRenderer: 'platform',
  },
  // Channel Metrics (4)
  {
    key: 'followers',
    label: 'Followers',
    sortable: true,
    align: 'right',
    width: '110px',
    defaultVisible: true,
    chartType: 'histogram',
    cellRenderer: 'number',
  },
  {
    key: 'totalPosts',
    label: 'Posts',
    sortable: true,
    align: 'right',
    width: '90px',
    defaultVisible: true,
    chartType: 'column',
    cellRenderer: 'number',
  },
  {
    key: 'totalLikes',
    label: 'Total Likes',
    sortable: true,
    align: 'right',
    width: '110px',
    defaultVisible: true,
    chartType: 'column',
    cellRenderer: 'number',
  },

  // Audience Metrics (2)
  {
    key: 'commentsReceived',
    label: 'Comments',
    sortable: true,
    align: 'right',
    width: '100px',
    defaultVisible: true,
    chartType: 'column',
    cellRenderer: 'number',
  },
  {
    key: 'sentiment',
    label: 'Sentiment',
    sortable: true,
    align: 'center',
    width: '100px',
    defaultVisible: true,
    chartType: 'gauge',
    cellRenderer: 'sentiment',
  },

  // Processing Metrics (4)
  {
    key: 'profilesAdded',
    label: 'Profiles Captured',
    sortable: true,
    align: 'right',
    width: '130px',
    defaultVisible: true,
    chartType: 'column',
    cellRenderer: 'number',
  },
  {
    key: 'profilesProcessed',
    label: 'Processed',
    sortable: true,
    align: 'right',
    width: '100px',
    defaultVisible: true,
    chartType: 'column',
    cellRenderer: 'number',
  },
  {
    key: 'brandsDetected',
    label: 'Brands',
    sortable: true,
    align: 'right',
    width: '90px',
    defaultVisible: true,
    chartType: 'column',
    cellRenderer: 'number',
  },
  {
    key: 'teamsDetected',
    label: 'Teams',
    sortable: true,
    align: 'right',
    width: '90px',
    defaultVisible: true,
    chartType: 'column',
    cellRenderer: 'number',
  },

  // Status & Control (2) - scrapingEnabled last for easy toggle access
  {
    key: 'lastActivity',
    label: 'Last Updated',
    sortable: true,
    align: 'left',
    width: '120px',
    defaultVisible: true,
    chartType: 'none',
    cellRenderer: 'date',
  },
] as const;

/** Column configs indexed by data source */
export const COLUMNS_BY_SOURCE: Readonly<Record<DataSourceId, ReadonlyArray<ColumnConfig>>> = {
  profiles: COLUMNS_PROFILES,
  content: COLUMNS_CONTENT,
  comments: COLUMNS_COMMENTS,
  brands: COLUMNS_BRANDS,
  hashtags: COLUMNS_HASHTAGS,
  teams: COLUMNS_TEAMS,
  channels: COLUMNS_CHANNELS,
};
